import { z } from 'zod'

/**
 * Les horaires d'ouverture, jour par jour (issue #158).
 *
 * Ils n'existaient que sous forme de trois lignes de texte libre — « Ouvert
 * 5/7 jours », « Lundi - Mardi - Mercredi - Vendredi - Samedi », « Jeudi -
 * Dimanche : Fermé » — saisies depuis le back-office. Elles disent quels jours
 * l'institut ouvre, **jamais à quelle heure**.
 *
 * Deux conséquences. Google ne peut pas afficher les horaires dans sa fiche
 * locale, alors que c'est la question la plus posée à un institut. Et le
 * balisage `DaySpa` reste muet : `openingHoursSpecification` exige `opens` et
 * `closes`, et les deviner ferait afficher « Ouvert » quand l'institut est
 * fermé — un visiteur qui se déplace pour rien, ce qui est pire que le silence.
 *
 * Le champ reste **optionnel** dans `practicalInfo` : tant qu'aucune plage
 * n'est saisie, tout se comporte comme avant. Les vraies plages doivent venir
 * de l'institut ; aucune valeur n'est inventée ici.
 */

export const JOURS = [
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
] as const

export type Jour = (typeof JOURS)[number]

/** Le vocabulaire Schema.org attend l'anglais, et l'URL complète. */
const JOURS_SCHEMA_ORG: Record<Jour, string> = {
  lundi: 'https://schema.org/Monday',
  mardi: 'https://schema.org/Tuesday',
  mercredi: 'https://schema.org/Wednesday',
  jeudi: 'https://schema.org/Thursday',
  vendredi: 'https://schema.org/Friday',
  samedi: 'https://schema.org/Saturday',
  dimanche: 'https://schema.org/Sunday',
}

const HEURE = /^([01]\d|2[0-3]):[0-5]\d$/

/** Minutes depuis minuit — la seule forme où deux heures se comparent. */
function enMinutes(heure: string): number {
  const [h, m] = heure.split(':').map(Number)
  return h * 60 + m
}

const creneauSchema = z
  .object({
    ouvre: z.string().regex(HEURE, 'Heure attendue au format HH:MM'),
    ferme: z.string().regex(HEURE, 'Heure attendue au format HH:MM'),
  })
  .refine(({ ouvre, ferme }) => enMinutes(ferme) > enMinutes(ouvre), {
    message: 'La fermeture doit suivre l’ouverture',
  })

/**
 * Un jour sans créneau est un jour fermé — c'est ce que Schema.org déduit de
 * l'absence, et cela évite un « fermé » à représenter autrement.
 */
const journeeSchema = z
  .object({
    jour: z.enum(JOURS),
    creneaux: z.array(creneauSchema),
  })
  .refine(
    ({ creneaux }) => {
      const tries = [...creneaux].sort((a, b) => enMinutes(a.ouvre) - enMinutes(b.ouvre))
      return tries.every(
        (creneau, index) => index === 0 || enMinutes(creneau.ouvre) >= enMinutes(tries[index - 1].ferme),
      )
    },
    { message: 'Deux créneaux de la même journée se chevauchent' },
  )

export const horairesSchema = z
  .array(journeeSchema)
  .refine((journees) => new Set(journees.map(({ jour }) => jour)).size === journees.length, {
    message: 'Un même jour est déclaré plusieurs fois',
  })

export type Creneau = z.infer<typeof creneauSchema>
export type Journee = z.infer<typeof journeeSchema>
export type Horaires = z.infer<typeof horairesSchema>

export interface SpecificationHoraire {
  '@type': 'OpeningHoursSpecification'
  dayOfWeek: string
  opens: string
  closes: string
}

/**
 * Le balisage que Google lit pour afficher les heures d'ouverture.
 *
 * Une entrée par créneau : un jour coupé entre midi et deux en produit deux,
 * ce qui est la façon dont Schema.org représente une pause. Un jour fermé n'en
 * produit aucune — le déclarer avec des heures nulles dirait autre chose que
 * l'absence.
 */
export function horairesEnSchemaOrg(horaires: Horaires | undefined): SpecificationHoraire[] {
  if (!horaires) return []

  return horaires.flatMap(({ jour, creneaux }) =>
    creneaux.map((creneau) => ({
      '@type': 'OpeningHoursSpecification' as const,
      dayOfWeek: JOURS_SCHEMA_ORG[jour],
      opens: creneau.ouvre,
      closes: creneau.ferme,
    })),
  )
}

/** « 09:00 » → « 9h », « 12:30 » → « 12h30 ». L'usage français, pas l'ISO. */
function heureLisible(heure: string): string {
  const [h, m] = heure.split(':')
  const heures = String(Number(h))
  return m === '00' ? `${heures}h` : `${heures}h${m}`
}

function majuscule(mot: string): string {
  return mot.charAt(0).toUpperCase() + mot.slice(1)
}

function memeJournee(a: Journee, b: Journee): boolean {
  if (a.creneaux.length !== b.creneaux.length) return false
  return a.creneaux.every(
    (creneau, index) =>
      creneau.ouvre === b.creneaux[index].ouvre && creneau.ferme === b.creneaux[index].ferme,
  )
}

/**
 * Les lignes telles qu'on les lit au pied de page.
 *
 * Les jours **consécutifs** de mêmes horaires sont regroupés : « Lundi –
 * mercredi : 9h – 19h » plutôt que trois lignes identiques. Les jours de
 * fermeture le sont aussi, mais sans exiger qu'ils se suivent — « Jeudi et
 * dimanche : fermé » est la façon dont on l'écrirait à la main.
 *
 * Cette fonction n'est branchée nulle part pour l'instant : elle attend que de
 * vraies plages soient saisies, et le changement d'affichage qui s'ensuivra
 * passe par la porte design.
 */
export function horairesEnLignes(horaires: Horaires | undefined): string[] {
  if (!horaires || horaires.length === 0) return []

  // L'ordre de la semaine, quel que soit celui de la saisie.
  const semaine = [...horaires].sort((a, b) => JOURS.indexOf(a.jour) - JOURS.indexOf(b.jour))

  const ouverts = semaine.filter(({ creneaux }) => creneaux.length > 0)
  const fermes = semaine.filter(({ creneaux }) => creneaux.length === 0)

  const lignes: string[] = []
  let groupe: Journee[] = []

  const ecrireGroupe = () => {
    if (groupe.length === 0) return
    const premier = groupe[0]
    const dernier = groupe[groupe.length - 1]
    const jours =
      groupe.length === 1
        ? majuscule(premier.jour)
        : `${majuscule(premier.jour)} – ${dernier.jour}`
    const plages = premier.creneaux
      .map(({ ouvre, ferme }) => `${heureLisible(ouvre)} – ${heureLisible(ferme)}`)
      .join(', ')
    lignes.push(`${jours} : ${plages}`)
    groupe = []
  }

  for (const journee of ouverts) {
    const precedent = groupe[groupe.length - 1]
    // Consécutifs dans la semaine ET de mêmes horaires : sinon on ferme le
    // groupe, pour ne pas écrire « Lundi – vendredi » quand jeudi est fermé.
    const suitLePrecedent =
      precedent && JOURS.indexOf(journee.jour) === JOURS.indexOf(precedent.jour) + 1
    if (precedent && (!suitLePrecedent || !memeJournee(precedent, journee))) ecrireGroupe()
    groupe.push(journee)
  }
  ecrireGroupe()

  if (fermes.length > 0) {
    const noms = fermes.map(({ jour }, index) => (index === 0 ? majuscule(jour) : jour))
    const liste =
      noms.length === 1 ? noms[0] : `${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`
    lignes.push(`${liste} : fermé`)
  }

  return lignes
}
